import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const STORAGE_KEY = 'rh_promo_banner_dismissed_v1';

const formatUntil = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
};

export default function PromoBanner() {
  const { promo } = useAuth();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isDismissed = localStorage.getItem(STORAGE_KEY) === promo.until;
    setDismissed(isDismissed);
  }, [promo.until]);

  if (!promo?.active || dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, promo.until || '1'); } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="relative w-full bg-gradient-to-r from-rose-tint/70 via-cream-paper to-rose-tint/70 border-b border-rose-tint"
      data-testid="promo-banner"
      role="region"
      aria-label="Promotional announcement"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-2 sm:gap-3">
        <Sparkles className="h-3.5 w-3.5 text-rose-gold shrink-0" />
        <p className="flex-1 text-xs sm:text-sm text-ink leading-snug">
          <span className="font-semibold">Free access</span>
          <span className="hidden sm:inline"> — all AI features unlocked </span>
          <span className="sm:hidden"> — all AI free </span>
          until <span className="font-semibold whitespace-nowrap">{formatUntil(promo.until)}</span>.
          <Link
            to="/ask"
            className="hidden sm:inline-flex items-center gap-1 ml-2 underline underline-offset-4 hover:text-rose-gold"
            data-testid="promo-banner-cta"
          >
            Try Ask AI <ArrowRight className="h-3 w-3" />
          </Link>
        </p>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss promotional banner"
          className="shrink-0 rounded-full p-1 hover:bg-rose-tint/60 transition-colors"
          data-testid="promo-banner-dismiss"
        >
          <X className="h-3.5 w-3.5 text-foreground/70" />
        </button>
      </div>
    </div>
  );
}

// Compact pill badge for use on individual pages (Ask/Regimen/Styling/Pricing)
export const PromoBadge = ({ className = '' }) => {
  const { promo } = useAuth();
  if (!promo?.active) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-rose-tint/60 border border-rose-tint px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-ink ${className}`}
      data-testid="promo-badge"
      title={`Free until ${formatUntil(promo.until)}`}
    >
      <Sparkles className="h-2.5 w-2.5 text-rose-gold" />
      Free until {formatUntil(promo.until)}
    </span>
  );
};

// Helper: returns whether promo is active (avoids needing useAuth in some spots)
export const usePromo = () => {
  const { promo } = useAuth();
  return promo || { active: false, until: null };
};
